import { Button, TimePicker } from 'antd'
import { format, parse } from 'date-fns'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import dateFnsGenerateConfig from 'rc-picker/lib/generate/dateFns'
import dayjs from 'dayjs'
import generatePicker from 'antd/es/date-picker/generatePicker'
import PropTypes from 'prop-types'

import 'antd/es/date-picker/style/index'
import './dateTimeNow.less'

const DatePicker = generatePicker(dateFnsGenerateConfig)

const DATE_FORMAT = 'dd.MM.yyyy'
const TIME_FORMAT = 'HH:mm'

const DateTimeNow = ({
  value,
  onChange,
  disabled = false,
  allowFuture = true,
  showNow = true,
  showTime = true,
}) => {
  const { t } = useTranslation()

  const valueFormat = showTime ? `${DATE_FORMAT} ${TIME_FORMAT}` : DATE_FORMAT

  const [dateValue, setDateValue] = useState(null)
  const [timeValue, setTimeValue] = useState(null)

  useEffect(() => {
    if (!value) {
      setDateValue(null)
      setTimeValue(null)

      return
    }

    const parsedDate = parse(value, valueFormat, new Date())

    if (!isNaN(parsedDate.getTime())) {
      setDateValue(parsedDate)
      setTimeValue(parsedDate)
    }
  }, [value, valueFormat])

  const emitChange = (date, time) => {
    if (!date || (showTime && !time)) {
      onChange('')

      return
    }

    if (!showTime) {
      onChange(format(date, valueFormat))

      return
    }

    const merged = new Date(date)

    merged.setHours(time.getHours())
    merged.setMinutes(time.getMinutes())
    merged.setSeconds(0)
    merged.setMilliseconds(0)

    onChange(format(merged, valueFormat))
  }

  const handleDateChange = date => {
    setDateValue(date)

    // Set time to the current time when a date is picked and no time has been selected yet
    if (showTime) {
      const effectiveTime = timeValue ?? new Date()

      if (!timeValue) {
        setTimeValue(effectiveTime)
      }

      emitChange(date, effectiveTime)
    } else {
      emitChange(date, null)
    }
  }

  const handleTimeChange = time => {
    const timeDate = time ? time.toDate() : null
    setTimeValue(timeDate)

    if (dateValue) {
      emitChange(dateValue, timeDate)
    }
  }

  const handleNow = () => {
    const now = new Date()

    setDateValue(now)

    if (showTime) {
      setTimeValue(now)
    }

    onChange(format(now, valueFormat))
  }

  const disabledDate = current => !allowFuture && current > new Date()

  return (
    <div className="date-time-now_container">
      {showNow && (
        <Button disabled={disabled} onClick={handleNow} type="primary">
          {t('common.now', 'Now')}
        </Button>
      )}

      <DatePicker
        allowClear={false}
        disabled={disabled}
        disabledDate={disabledDate}
        format={DATE_FORMAT}
        onChange={handleDateChange}
        style={{ width: 130 }}
        value={dateValue}
      />

      {showTime && (
        <TimePicker
          allowClear={false}
          disabled={disabled}
          format={TIME_FORMAT}
          minuteStep={1}
          onChange={handleTimeChange}
          style={{ width: 90 }}
          value={timeValue ? dayjs(timeValue) : null}
        />
      )}
    </div>
  )
}

DateTimeNow.propTypes = {
  allowFuture: PropTypes.bool,
  disabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  showNow: PropTypes.bool,
  showTime: PropTypes.bool,
  value: PropTypes.string,
}

DateTimeNow.defaultProps = {
  allowFuture: true,
  disabled: false,
  showNow: true,
  showTime: true,
  value: '',
}

export default DateTimeNow
